import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, CheckCircle, Pencil } from 'lucide-react';
import { changePassword, updateProfile, selectUser, selectAuthStatus, selectAuthError, clearError } from '../features/auth/authSlice';
import SEO from './SEO';
import './ProfilePage.css';

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState('');

  // Get stored user data to check auth type
  const storedUser = JSON.parse(localStorage.getItem('cleanStudioUser') || '{}');
  const isEmailAuth = storedUser.authType === 'email';
  const isGoogleAuth = storedUser.authType === 'google';

  const handleSaveName = async () => {
    setNameError('');
    setNameSuccess('');
    dispatch(clearError());

    const trimmed = editName.trim();
    if (!trimmed) {
      setNameError('Name cannot be empty');
      return;
    }
    if (trimmed.length > 100) {
      setNameError('Name must be 100 characters or less');
      return;
    }
    if (trimmed === user?.name) {
      setIsEditingName(false);
      return;
    }

    try {
      await dispatch(updateProfile({ name: trimmed })).unwrap();
      setNameSuccess('Name updated successfully!');
      setIsEditingName(false);
      setTimeout(() => setNameSuccess(''), 3000);
    } catch (err) {
      setNameError(err || 'Failed to update name');
    }
  };

  const handleCancelEditName = () => {
    setEditName(user?.name || '');
    setIsEditingName(false);
    setNameError('');
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setValidationError('');
    setSuccessMessage('');
    dispatch(clearError());

    // Validate current password is entered
    if (!currentPassword) {
      setValidationError('Please enter your current password');
      return;
    }

    // Validate new password
    const pwdError = validatePassword(newPassword);
    if (pwdError) {
      setValidationError(pwdError);
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setValidationError('New passwords do not match');
      return;
    }

    try {
      await dispatch(changePassword({ currentPassword, newPassword })).unwrap();
      setSuccessMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password failed:', err);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <>
      <SEO
        title="Profile - myCleanStudio"
        description="Manage your myCleanStudio account settings"
        url="/dashboard/profile"
      />
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-header">
            <div className="profile-icon">
              <User size={32} color="white" />
            </div>
            <h2 className="profile-title">Your Profile</h2>
            <p className="profile-subtitle">Manage your account settings</p>
          </div>

          {/* User Info Section */}
          <div className="profile-section">
            <h3 className="section-title">Account Information</h3>

            {nameSuccess && (
              <div className="success-message">
                <CheckCircle size={16} />
                {nameSuccess}
              </div>
            )}
            {nameError && (
              <div className="error-message">
                {nameError}
              </div>
            )}

            <div className="info-row">
              <span className="info-label">Name</span>
              {isEditingName ? (
                <div className="name-edit-controls">
                  <input
                    type="text"
                    className="name-edit-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEditName();
                    }}
                    disabled={status === 'loading'}
                    autoFocus
                    maxLength={100}
                  />
                  <button
                    className="name-save-btn"
                    onClick={handleSaveName}
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="name-cancel-btn"
                    onClick={handleCancelEditName}
                    disabled={status === 'loading'}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <span className="info-value info-value-editable" onClick={() => setIsEditingName(true)}>
                  {user.name || 'Not set'}
                  <Pencil size={14} className="edit-icon" />
                </span>
              )}
            </div>
            <div className="info-row">
              <span className="info-label">Email</span>
              <span className="info-value">{user.email || 'Not set'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Sign-in method</span>
              <span className="info-value">{isGoogleAuth ? 'Google' : isEmailAuth ? 'Email & Password' : 'Unknown'}</span>
            </div>
          </div>

          {/* Password Change Section - Only for email auth users */}
          {isEmailAuth && (
            <div className="profile-section">
              <h3 className="section-title">
                <Lock size={18} />
                Change Password
              </h3>

              {successMessage && (
                <div className="success-message">
                  <CheckCircle size={16} />
                  {successMessage}
                </div>
              )}

              {(error || validationError) && (
                <div className="error-message">
                  {validationError || error}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="password-form">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      disabled={status === 'loading'}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      tabIndex={-1}
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      disabled={status === 'loading'}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="password-hint">
                    Must include uppercase, lowercase, and a number
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      disabled={status === 'loading'}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <>
                      <span className="btn-spinner"></span>
                      Changing password...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
